import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout } from "../reducers/auth";
import { TENANCY_API_URL } from 'config';

const baseQuery = fetchBaseQuery({
    baseUrl: TENANCY_API_URL,
    prepareHeaders: (headers) => {
        const user = localStorage.getItem('user');

        if (user) {
            let userToken = JSON.parse(user).token;
            if (userToken) {
                headers.set('authorization', `Bearer ${userToken}`)
            }
        }
        return headers;
    },
    credentials: "omit"
})

const baseQueryWithReauth = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions)
    if (result.error && result.error.status === 401) {
        // I don't think this is the right endpoint to call
        // try to get a new token
        // const refreshResult = await baseQuery('auth/login', api, extraOptions)
        // if (refreshResult.data) {
        //     // store the new token
        //     api.dispatch(token(refreshResult.data))
        //     // retry the initial query
        //     result = await baseQuery(args, api, extraOptions)
        // } else {
        api.dispatch(logout())
        // }
    }
    return result
}

// Map logging tab values to API endpoint suffixes
const auditTypeToEndpoint = {
    'OnDemand': 'ondemand',
    'UserCreation': 'usercreation',
    'SendReminder': 'sendreminder',
    'UserManagement': 'usermanagement'
};


export const tenancyApi = createApi({
    reducerPath: "tenancyApi",
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Tenancy', 'TenancyDataBasics', 'Locations', 'States', 'CompanyNames', 'Cities', 'Departments', 'Countries', 'LineManagers', 'AuditLogs', 'FeatureSwitches', 'ContentLastSent', 'Users', 'UserReportingData', 'Allowlisting', 'SyncFields'],
    endpoints: (builder) => ({
        generateNewAccessKey: builder.mutation({
            query: ({ tenancyId }) => ({
                url: `tenancy/${tenancyId}/generatekey`,
                method: 'POST',
            }),
            transformResponse: (response) => response.Data,
            transformErrorResponse: (response) => response.status,
            invalidatesTags: (result, error, id) => [{ type: "Tenancy", id: id }]
        }),
        getTenancyById: builder.query({
            query: ({ tenancyId }) => `tenancy/${tenancyId}`,
            transformResponse: (response) => response.data,
            transformErrorResponse: (response) => response.status,
            providesTags: (result, error, id) => [{ type: "Tenancy", id }]
        }),
        getTenancies: builder.query({
            // Changed 'query' to 'searchTerm' to match backend parameter name
            query: ({ perPage, page, includes, searchTerm, additionalQueryParams }) =>
                `tenancy/?perPage=${perPage}&page=${page}&includes=${includes}&searchTerm=${encodeURIComponent(searchTerm || '')}&verbose=false${additionalQueryParams}`,
            transformResponse: (response) => response.data,
            transformErrorResponse: (response) => response.status,
            providesTags: (result) => {
                return result ?
                    [...result.map(({ id }) => ({ type: "Tenancy", id })), { type: "Tenancy", id: "LIST" }]
                    :
                    [{ type: "Tenancy", id: "LIST" }];
            }
        }),
        getLocationsByTenancyId: builder.query({
            query: ({ tenancyId }) => `tenancy/${tenancyId}/meta/location`,
            transformResponse: (response) => response.filter(name => name !== "" && name !== "-"),
            providesTags: (result, error, tenancyId) => [{ type: "Locations", tenancyId }]
        }),
        getStatesByTenancyId: builder.query({
            query: ({ tenancyId }) => `tenancy/${tenancyId}/meta/state`,
            transformResponse: (response) => response.filter(name => name !== "" && name !== "-"),
            providesTags: (result, error, tenancyId) => [{ type: "States", tenancyId }]
        }),
        getCompanyNamesByTenancyId: builder.query({
            query: ({ tenancyId }) => `tenancy/${tenancyId}/meta/companyname`,
            transformResponse: (response) => response.filter(name => name !== "" && name !== "-"),
            providesTags: (result, error, tenancyId) => [{ type: "CompanyNames", tenancyId }]
        }),
        getCitiesByTenancyId: builder.query({
            query: ({ tenancyId }) => `tenancy/${tenancyId}/meta/city`,
            transformResponse: (response) => response.filter(name => name !== "" && name !== "-"),
            providesTags: (result, error, tenancyId) => [{ type: "Cities", tenancyId }]
        }),
        getDepartmentsByTenancyId: builder.query({
            query: ({ tenancyId }) => `tenancy/${tenancyId}/meta/department`,
            transformResponse: (response) => response.filter(name => name !== "" && name !== "-"),
            providesTags: (result, error, tenancyId) => [{ type: "Departments", tenancyId }]
        }),
        getCountriesByTenancyId: builder.query({
            query: ({ tenancyId }) => `tenancy/${tenancyId}/meta/country`,
            transformResponse: (response) => response.filter(name => name !== "" && name !== "-"),
            providesTags: (result, error, tenancyId) => [{ type: "Countries", tenancyId }]
        }),
        getLineManagersByTenancyId: builder.query({
            query: ({ tenancyId }) => `tenancy/${tenancyId}/meta/linemanager`,
            transformResponse: (response) => response.filter(name => name !== "" && name !== "-"),
            providesTags: (result, error, tenancyId) => [{ type: "LineManagers", tenancyId }]
        }),
        // old endpoint kept for compatibility
        getAuditLogsForTenancy: builder.query({
            query: ({ tenancyId }) => `tenancy/${tenancyId}/audit`,
            transformResponse(baseQueryReturnValue) {
                return baseQueryReturnValue;
            },
            transformErrorResponse: (response) => response.status,
        }),
        getAuditLogsByType: builder.query({
            query: ({ tenancyId, auditType, limit = 100, nextToken = null, status = null }) => {
                const endpoint = auditTypeToEndpoint[auditType] || auditType.toLowerCase();
                let url = `tenancy/${tenancyId}/audit/${endpoint}?limit=${limit}`;
                if (nextToken) {
                    url += `&nextToken=${encodeURIComponent(nextToken)}`;
                }
                if (status) {
                    url += `&status=${encodeURIComponent(status)}`;
                }
                return url;
            },
            // Cache by tenancyId + auditType + status combination
            serializeQueryArgs: ({ queryArgs }) => {
                return `${queryArgs.tenancyId}-${queryArgs.auditType}-${queryArgs.status || ''}`;
            },
            // Merge pages together
            merge: (currentCache, newData, { arg }) => {
                if (!arg.nextToken) {
                    // Fresh request, replace cache
                    return newData;
                }
                // Append new items
                return {
                    ...newData,
                    items: [...(currentCache?.items || []), ...(newData.items || [])]
                };
            },
            forceRefetch: ({ currentArg, previousArg }) => {
                return currentArg?.nextToken !== previousArg?.nextToken;
            },
            providesTags: (result, error, { tenancyId, auditType }) => [
                { type: 'AuditLogs', id: `${tenancyId}-${auditType}` }
            ]
        }),
        getImportStatus: builder.query({
            query: ({ tenancyId, all }) => all ?
                `tenancy/${tenancyId}/importstatus?all=true` :
                `tenancy/${tenancyId}/importstatus`,
            transformResponse(baseQueryReturnValue) {
                return baseQueryReturnValue;
            },
            transformErrorResponse: (response) => response.status,
        }),
        createGoogleImport: builder.query({
            query: ({ code, email, tenancyId }) => ({
                url: `tenancy/${tenancyId}/googleimport?code=${code}&requestEmail=${email}`,
                method: 'GET',
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
        }),
        getTenancyBasics: builder.query({
            queryFn: async ({ tenancyId }, api, extraOptions, baseQuery) => {
                // Check if tenancyId is null, undefined, empty string, etc.
                if (!tenancyId) {
                    // Return null without making an API call
                    return { data: null };
                }
                const resp = await baseQuery(`tenancy/${tenancyId}/basic`, api, extraOptions);
                return resp;
            },
            transformResponse: (response) => response,
            transformErrorResponse: (response, meta, arg) => {
                // Check if the error status is 401 (Unauthorized)
                if (response.status === 401) {
                    // We are logged out - redirect to login page
                    sessionStorage.clear();
                    localStorage.clear();
                    debugger
                    window.location.href = '/';
                }

                return response.status;
            },
            providesTags: (result, error, id) => [{ type: "TenancyDataBasics", id }]
        }),
        getReminderOptions: builder.query({
            query: ({ tenancyId }) => `tenancyreminder?tenancyId=${tenancyId}`,
        }),
        outlookPluginHandler: builder.query({
            query: ({ tenancyId, tenancy }) => ({
                url: `tenancy/${tenancyId}/outlookpluginhandler`,
                params: {
                    tenancy,
                }
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
        }),
        getCachedOfficeData: builder.query({
            queryFn: async ({ tenancyId }, api, extraOptions, baseQuery) => {
                const cachedDataResponse = await baseQuery(`office365/cached?tenancyId=${tenancyId}`, api, extraOptions);

                if (cachedDataResponse.error) {
                    return { error: cachedDataResponse.error };
                }

                let data = cachedDataResponse.data;

                if (data && data.GroupLink) {
                    const groupLinkResponse = await fetch(data.GroupLink, {
                        method: "GET",
                    });

                    if (groupLinkResponse.ok) {
                        data = { Groups: await groupLinkResponse.json() };
                    }
                }

                return { data };
            },
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
        }),
        createOnDemand: builder.mutation({
            async queryFn(arg, api, extraOptions, baseQuery) {
                const { data, tenancyId } = arg;
                const maxRetries = 6;
                const baseDelay = 1000;

                const exponentialBackoffWithJitter = (attempt) => {
                    const exponentialDelay = Math.min(Math.pow(2, attempt) * baseDelay, 30000); // Max delay of 30 seconds
                    const jitter = Math.random();
                    return Math.floor(exponentialDelay * (1 + jitter));
                };

                for (let attempt = 0; attempt < maxRetries; attempt++) {
                    try {
                        const result = await baseQuery({
                            url: `tenancy/${tenancyId}/ondemand`,
                            method: 'POST',
                            body: data
                        }, api, extraOptions);

                        if (result.error) {
                            throw result.error;
                        }

                        return { data: result.data };
                    } catch (error) {
                        console.error(`Attempt ${attempt + 1} failed:`, error);

                        if (attempt === maxRetries - 1) {
                            // Log the final error with more details
                            console.error('All retry attempts failed:', {
                                tenancyId,
                                error: error.toString(),
                                status: error.status,
                                data: error.data
                            });
                            return { error };
                        }

                        const delay = exponentialBackoffWithJitter(attempt);
                        await new Promise(resolve => setTimeout(resolve, delay));
                    }
                }
            },
            invalidatesTags: [{ type: "Tenancy", id: "LIST" }]
        }),
        createTenancy: builder.mutation({
            query: ({ data }) => ({
                url: `tenancy`,
                method: 'POST',
                body: data
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
            invalidatesTags: [{ type: "Tenancy", id: "LIST" }]
        }),
        createOfficeTenantImport: builder.mutation({
            query: ({ tenancyId, tenant, email }) => ({
                url: `tenancy/${tenancyId}/office365import?tenant=${tenant}&email=${email}`,
                method: 'POST',
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
        }),

        updateTenancy: builder.mutation({
            query: ({ data, tenancyId }) => ({
                url: `tenancy/${tenancyId}`,
                method: 'PUT',
                body: data
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
            invalidatesTags: (result, error, id) => [{ type: "Tenancy", id: "LIST" }, { type: "Tenancy", id: id }]
        }),
        // Its own endpoint, not the generic PUT: the backend checks the tenancy owns the domain.
        // An empty domain disconnects.
        updateGoogleWorkspaceDomain: builder.mutation({
            query: ({ tenancyId, domain }) => ({
                url: `tenancy/${tenancyId}/googleworkspacedomain`,
                method: 'PUT',
                body: { domain }
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.data?.errorMessage || "Couldn't connect that domain.",
            invalidatesTags: (result, error, { tenancyId }) => [{ type: "Tenancy", id: "LIST" }, { type: "Tenancy", id: tenancyId }]
        }),
        // Its own endpoint too, so other settings tabs' saves can't overwrite it. No theme resets
        // to the defaults.
        updatePhishReportTheme: builder.mutation({
            query: ({ tenancyId, theme }) => ({
                url: `tenancy/${tenancyId}/phishreporttheme`,
                method: 'PUT',
                body: theme
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.data?.errorMessage || "Couldn't save the add-on theme.",
            invalidatesTags: (result, error, { tenancyId }) => [{ type: "Tenancy", id: "LIST" }, { type: "Tenancy", id: tenancyId }]
        }),
        updateTenancyLogo: builder.mutation({
            queryFn: async ({ data, tenancyId }, api, extraOptions, baseQuery) => {
                const allowedContentTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
                const contentType = data.file?.type;
                if (!allowedContentTypes.includes(contentType)) {
                    return { error: { status: 400, data: 'File type not allowed. Please upload a PNG, JPEG, GIF, or WebP image.' } };
                }

                const preSignedUrlResponse = await baseQuery({
                    url: `tenancy/${tenancyId}/sign-upload`,
                    method: 'POST',
                    body: { contentType }
                }, api, extraOptions);

                if (preSignedUrlResponse.error) {
                    return { error: preSignedUrlResponse.error };
                }

                const { presignedUrl, fileName } = preSignedUrlResponse.data;

                const uploadResponse = await fetch(presignedUrl, {
                    method: "PUT",
                    body: data.file,
                });

                if (!uploadResponse.ok) {
                    return { error: { status: uploadResponse.status, statusText: uploadResponse.statusText } };
                }

                return { data: { success: true, fileName } };
            },
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
            invalidatesTags: (result, error, { tenancyId }) => [{ type: "Tenancy", id: "LIST" }, { type: "Tenancy", id: tenancyId }]
        }),

        updateReminderOptions: builder.mutation({
            query: ({ data }) => ({
                url: `tenancyreminder`,
                method: 'POST',
                body: data
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
            invalidatesTags: (result, error, id) => [{ type: "Tenancy", id: "LIST" }, { type: "Tenancy", id: id }]
        }),
        // TODO: Update the endpoint from user to tenancy in 2.0
        updateAutoSync: builder.mutation({
            query: ({ tenancyId, data }) => ({
                url: `user/office365?tenancyId=${tenancyId}`,
                method: 'POST',
                body: data
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
            invalidatesTags: (result, error, id) => [{ type: "Tenancy", id: "LIST" }, { type: "Tenancy", id: id }]
        }),
        updateSyncFiltering: builder.mutation({
            query: ({ tenancyId, syncInactiveUserAction }) => ({
                url: `tenancy/${tenancyId}/sync-filtering`,
                method: 'PUT',
                body: { SyncInactiveUserAction: syncInactiveUserAction }
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
            invalidatesTags: (result, error, { tenancyId }) => [{ type: "Tenancy", id: "LIST" }, { type: "Tenancy", id: tenancyId }]
        }),
        getSyncFields: builder.query({
            query: ({ tenancyId }) => `tenancy/${tenancyId}/sync-fields`,
            transformResponse: (response) => response.data,
            transformErrorResponse: (response) => response.status,
            providesTags: (result, error, { tenancyId }) => [{ type: "SyncFields", id: tenancyId }]
        }),
        updateSyncFields: builder.mutation({
            query: ({ tenancyId, disabledFields }) => ({
                url: `tenancy/${tenancyId}/sync-fields`,
                method: 'PUT',
                body: { disabledFields }
            }),
            transformResponse: (response) => response.data,
            // Keep the server message: it names the field a caller tried to disable but could not.
            transformErrorResponse: (response) => response.data?.message ?? response.status,
            invalidatesTags: (result, error, { tenancyId }) => [{ type: "SyncFields", id: tenancyId }]
        }),
        refreshSyncGroups: builder.mutation({
            query: ({ tenancyId, data }) => ({
                url: `tenancy/${tenancyId}/refresh-groups`,
                method: 'POST',
                body: data
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
        }),
        deleteTenancyGroup: builder.mutation({
            query: ({ tenancyId, groupId }) => ({
                url: `tenancy/${tenancyId}/group/${groupId}`,
                method: 'DELETE'
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
            invalidatesTags: (result, error, { tenancyId }) => [{ type: "TenancyDataBasics", id: tenancyId }]
        }),
        deleteAllUsers: builder.mutation({
            query: ({ tenancyId }) => `tenancy/${tenancyId}/purgeusers`,
            invalidatesTags: (result, error, id) => [{ type: "Tenancy", id: "LIST" }, { type: "Tenancy", id: id }]
        }),
        deleteTenancy: builder.mutation({
            query: ({ tenancyId }) => ({
                url: `tenancy/${tenancyId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, id) => [{ type: "Tenancy", id: "LIST" }, { type: "Tenancy", id: id }]
        }),
        deleteJourneysAndSchedules: builder.mutation({
            query: ({ tenancyId }) => ({
                url: `tenancy/${tenancyId}/deleteAssociatedJourneysAndSchedules`,
                method: 'DELETE',
            }),
            transformResponse: (response) => response.data,
            transformErrorResponse: (response) => response.status,
            invalidatesTags: (result, error, id) => [{ type: "Tenancy", id: "LIST" }, { type: "Tenancy", id: id }]
        }),
        getTenancyAllowRetake: builder.query({
            query: ({ tenancyId }) => `tenancycourse/${tenancyId}`,
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
        }),
        // Allowlisting Sync (BD-740). Served by auth-service under the same base URL.
        // The tenancy is taken from the JWT, so neither call passes an id.
        getAllowlistingStatus: builder.query({
            query: () => `allowlisting/status`,
            // auth-service serialises this with default Newtonsoft settings, so the wire
            // shape is PascalCase. Normalised here rather than read raw so the component
            // keeps working if the endpoint is switched to camelCase later.
            transformResponse: (response) => ({
                connected: response?.Connected ?? response?.connected ?? false,
                tenantId: response?.TenantId ?? response?.tenantId ?? null,
                onMicrosoftDomain: response?.OnMicrosoftDomain ?? response?.onMicrosoftDomain ?? null,
                runStatus: response?.RunStatus ?? response?.runStatus ?? null,
                lastRunAt: response?.LastRunAt ?? response?.lastRunAt ?? null,
                lastRunDetail: response?.LastRunDetail ?? response?.lastRunDetail ?? null,
                manualDomains: response?.ManualDomains ?? response?.manualDomains ?? [],
                manualSendingIps: response?.ManualSendingIps ?? response?.manualSendingIps ?? [],
            }),
            transformErrorResponse: (response) => response.status,
            providesTags: ['Allowlisting'],
        }),
        // Removes everything we applied to the customer's Exchange Online and forgets
        // the tenancy. Asynchronous like a run, so the status panel reports the outcome.
        revokeAllowlisting: builder.mutation({
            query: () => ({
                url: `allowlisting/revoke`,
                method: 'POST',
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
            invalidatesTags: ['Allowlisting'],
        }),
        getFeatureSwitches: builder.query({
            query: ({ tenancyId }) => `tenancy/${tenancyId}/featureswitches`,
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
            providesTags: (result, error, { tenancyId }) => [{ type: "FeatureSwitches", id: tenancyId }]
        }),
        getContentLastSent: builder.query({
            query: ({ tenancyId }) => `tenancy/${tenancyId}/content-last-sent`,
            transformResponse: (response) => {
                const map = {};
                (Array.isArray(response) ? response : []).forEach(item => {
                    map[`${item.sentType}#${item.sentTypeName}`] = {
                        lastSentDateTime: item.lastSentDateTime,
                        timesSent: item.timesSent
                    };
                });
                return map;
            },
            providesTags: (result, error, { tenancyId }) => [
                { type: 'ContentLastSent', id: tenancyId }
            ]
        }),
        updateFeatureSwitches: builder.mutation({
            query: ({ tenancyId, data }) => ({
                url: `tenancy/${tenancyId}/featureswitches`,
                method: 'PUT',
                body: data
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
            // Optimistically update the cache with the expected result
            // This helps UI feel more responsive while waiting for server
            async onQueryStarted({ tenancyId, data }, { dispatch, queryFulfilled }) {
                // We'll immediately invalidate ALL feature switches and tenancy data
                // after the mutation completes, so just use a simple optimistic update

                // Direct optimistic update to feature switches
                const patchResult = dispatch(
                    tenancyApi.util.updateQueryData(
                        'getFeatureSwitches',
                        { tenancyId },
                        (draft) => {
                            // Update raw values
                            if (data.enableReporting !== undefined) {
                                draft.enableReporting = data.enableReporting;
                                // If turning off, also update effective immediately
                                if (data.enableReporting === false) {
                                    draft.effectiveEnableReporting = false;
                                }
                            }

                            if (data.enableReportsB !== undefined) {
                                draft.enableReportsB = data.enableReportsB;
                            }

                            if (data.enableOndemand !== undefined) {
                                draft.enableOndemand = data.enableOndemand;
                                // If turning off, also update effective immediately
                                if (data.enableOndemand === false) {
                                    draft.effectiveEnableOndemand = false;
                                }
                            }

                            if (data.enableCourseRetake !== undefined) {
                                draft.enableCourseRetake = data.enableCourseRetake;
                                // If turning off, also update effective immediately
                                if (data.enableCourseRetake === false) {
                                    draft.effectiveEnableCourseRetake = false;
                                }
                            }
                        }
                    )
                );

                // Also update tenancy data optimistically
                const tenancyPatchResult = dispatch(
                    tenancyApi.util.updateQueryData(
                        'getTenancyById',
                        { tenancyId },
                        (draft) => {
                            if (!draft) return;

                            // Apply the same changes to tenancy data
                            if (data.enableReporting !== undefined) {
                                draft.enableReporting = data.enableReporting;
                            }
                            if (data.enableReportsB !== undefined) {
                                draft.enableReportsB = data.enableReportsB;
                            }
                            if (data.enableOndemand !== undefined) {
                                draft.enableOndemand = data.enableOndemand;
                            }
                            if (data.enableCourseRetake !== undefined) {
                                draft.enableCourseRetake = data.enableCourseRetake;
                            }
                        }
                    )
                );

                try {
                    // Wait for the mutation to finish
                    await queryFulfilled;

                    // Force refresh ALL feature switches and tenancy data
                    // This is a more aggressive approach but guarantees UI consistency
                    dispatch(tenancyApi.util.invalidateTags([
                        { type: "FeatureSwitches", id: "LIST" },
                        { type: "Tenancy", id: "LIST" }
                    ]));
                } catch {
                    // If it fails, revert the optimistic updates
                    patchResult.undo();
                    tenancyPatchResult.undo();
                }
            },
            invalidatesTags: (result, error, { tenancyId }) => [
                { type: "FeatureSwitches", id: tenancyId },
                { type: "Tenancy", id: tenancyId }
            ]
        }),
        refreshReports: builder.mutation({
            query: (tenancyId) => ({
                url: `reporting/refresh`,
                method: 'GET',
                params: { tenancyId },
                responseHandler: 'text',
            }),
            transformResponse: (response) => {
                return { message: response };
            },
            invalidatesTags: [
                { type: 'UserReportingData', id: 'LIST' },
                { type: "Tenancy", id: "LIST" }
            ]
        }),
        setLineManagerReportingLevel: builder.mutation({
            query: ({ tenancyId }) => ({
                url: `tenancy/${tenancyId}/set-line-manager-reporting-level`,
                method: 'POST',
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
            invalidatesTags: (result, error, { tenancyId }) => [
                { type: "Users", id: "LIST" },
                { type: "Tenancy", id: tenancyId }
            ]
        }),
        setLineManagerOnDemandLevel: builder.mutation({
            query: ({ tenancyId }) => ({
                url: `tenancy/${tenancyId}/set-line-manager-ondemand-level`,
                method: 'POST',
            }),
            transformResponse: (response) => response,
            transformErrorResponse: (response) => response.status,
            invalidatesTags: (result, error, { tenancyId }) => [
                { type: "Users", id: "LIST" },
                { type: "Tenancy", id: tenancyId }
            ]
        }),
        getDmiSettings: builder.query({
            query: ({ tenancyId }) => `tenancy/${tenancyId}/dmi-settings`,
            transformResponse: (response) => response.data,
            transformErrorResponse: (response) => response.status,
            providesTags: (result, error, { tenancyId }) => [{ type: "DmiSettings", id: tenancyId }]
        }),
        updateDmiSettings: builder.mutation({
            query: ({ tenancyId, data }) => ({
                url: `tenancy/${tenancyId}/dmi-settings`,
                method: 'PUT',
                body: data
            }),
            transformResponse: (response) => response.data,
            transformErrorResponse: (response) => response.status,
            invalidatesTags: (result, error, { tenancyId }) => [
                { type: "DmiSettings", id: tenancyId }
            ]
        }),
    }),
});

export const {
    useGenerateNewAccessKeyMutation,
    useGetTenancyByIdQuery,
    useGetTenanciesQuery,
    useLazyGetTenanciesQuery,
    useGetTenancyBasicsQuery,
    useLazyGetDepartmentsByTenancyIdQuery,
    useLazyGetLocationsByTenancyIdQuery,
    useLazyGetCitiesByTenancyIdQuery,
    useLazyGetCountriesByTenancyIdQuery,
    useLazyGetStatesByTenancyIdQuery,
    useLazyGetCompanyNamesByTenancyIdQuery,
    useLazyGetLineManagersByTenancyIdQuery,
    useGetDepartmentsByTenancyIdQuery,
    useGetLocationsByTenancyIdQuery,
    useGetCitiesByTenancyIdQuery,
    useGetCountriesByTenancyIdQuery,
    useGetStatesByTenancyIdQuery,
    useGetCompanyNamesByTenancyIdQuery,
    useGetLineManagersByTenancyIdQuery,
    useGetAuditLogsForTenancyQuery,
    useGetAuditLogsByTypeQuery,
    useLazyGetAuditLogsByTypeQuery,
    useGetImportStatusQuery,
    useGetReminderOptionsQuery,
    useGetCachedOfficeDataQuery,
    useCreateOnDemandMutation,
    useCreateTenancyMutation,
    useCreateOfficeTenantImportMutation,
    useUpdateTenancyMutation,
    useUpdateGoogleWorkspaceDomainMutation,
    useUpdatePhishReportThemeMutation,
    useUpdateReminderOptionsMutation,
    useUpdateTenancyLogoMutation,
    useUpdateAutoSyncMutation,
    useUpdateSyncFilteringMutation,
    useRefreshSyncGroupsMutation,
    useDeleteTenancyGroupMutation,
    useDeleteAllUsersMutation,
    useDeleteTenancyMutation,
    useDeleteJourneysAndSchedulesMutation,
    useGetTenancyAllowRetakeQuery,
    useGetAllowlistingStatusQuery,
    useRevokeAllowlistingMutation,
    useGetSyncFieldsQuery,
    useUpdateSyncFieldsMutation,
    useLazyCreateGoogleImportQuery,
    useRefreshReportsMutation,
    useOutlookPluginHandlerQuery,
    useGetFeatureSwitchesQuery,
    useGetContentLastSentQuery,
    useUpdateFeatureSwitchesMutation,
    useSetLineManagerReportingLevelMutation,
    useSetLineManagerOnDemandLevelMutation,
    useGetDmiSettingsQuery,
    useUpdateDmiSettingsMutation,
} = tenancyApi;